import { useMemo, useState } from "react";
import { columns, deepReads, hotRadar } from "@/mocks/editorial";

// 栏目专题区
export function Columns() {
  return (
    <section id="columns" className="bg-paper-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-ember-600">内幕栏目 COLUMNS</p>
            <h2 className="font-display text-2xl font-black text-ink-900 md:text-4xl">四大议题，层层拆解</h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-ink-500">
            用行业常识与传播学视角，把综艺幕后的常见现象归类讲清楚。
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {columns.map((c) => (
            <a
              key={c.id}
              href="#deep"
              className="group flex flex-col rounded-2xl border border-paper-200 bg-paper-100 p-6 transition hover:-translate-y-1 hover:border-ember-500/40"
            >
              <span
                className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl ${c.theme} text-paper-50`}
              >
                <i className={`${c.icon} text-xl`} />
              </span>
              <h3 className="font-display text-lg font-bold text-ink-900">{c.name}</h3>
              <p className="mt-1 text-xs italic text-ember-700">— {c.slogan}</p>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-600">{c.desc}</p>
              <div className="mt-5 flex items-center justify-between border-t border-paper-300 pt-4">
                <span className="text-xs text-ink-500">{c.count}</span>
                <span className="flex items-center gap-1 text-xs font-semibold text-ember-700 transition group-hover:gap-2">
                  进入栏目 <i className="ri-arrow-right-line" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// 热搜速递清单
export function RadarFeed() {
  const all = [...hotRadar, ...hotRadar];
  return (
    <section id="radar" className="bg-paper-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-ember-600">热点速递 FEED</p>
            <h2 className="font-display text-2xl font-black text-ink-900 md:text-3xl">瓜田滚动，实时追踪</h2>
          </div>
          <span className="hidden items-center gap-2 text-sm text-ink-500 md:flex">
            <i className="ri-loop-right-line text-ember-600" /> 信息整理自公开讨论
          </span>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {all.slice(0, 8).map((h, i) => (
            <a
              key={i}
              href="#deep"
              className="group flex items-center gap-4 rounded-xl border border-paper-200 bg-paper-50 p-4 transition hover:border-ember-500/40"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-ink-800 text-sm font-black text-paper-50">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-medium leading-relaxed text-ink-800 transition group-hover:text-ember-700">
                  {h.title}
                </p>
                <div className="mt-2 flex items-center gap-2 text-xs text-ink-500">
                  <span className="rounded bg-ember-100 px-2 py-0.5 text-ember-700">{h.tag}</span>
                  <span>热度 {h.heat}</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// 深读专栏 + 筛选
export function DeepRead() {
  const cats = ["全部", ...columns.map((c) => c.name)];
  const [cat, setCat] = useState("全部");
  const list = useMemo(
    () => (cat === "全部" ? deepReads : deepReads.filter((d) => d.col === cat)),
    [cat]
  );

  return (
    <section id="deep" className="bg-paper-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-ember-600">深读专栏 DEEP DIVE</p>
            <h2 className="font-display text-2xl font-black text-ink-900 md:text-4xl">不止吃瓜，更讲门道</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition ${
                  cat === c
                    ? "bg-ember-600 text-paper-50"
                    : "bg-paper-150 text-ink-600 hover:bg-ember-100 hover:text-ember-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((d, i) => (
            <article
              key={i}
              className="group flex flex-col overflow-hidden rounded-2xl border border-paper-200 bg-paper-100 transition hover:-translate-y-1"
            >
              <div className={`flex h-40 items-center justify-center ${i % 2 === 0 ? "bg-ink-800" : "bg-ember-800"}`}>
                <i
                  className={`${
                    i % 3 === 0 ? "ri-movie-2-line" : i % 3 === 1 ? "ri-scissors-2-line" : "ri-goblet-line"
                  } text-5xl text-paper-50/80`}
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-2 text-xs text-ink-500">
                  <span className="rounded bg-paper-300 px-2 py-0.5 font-semibold text-ink-700">{d.col}</span>
                  <span>{d.date}</span>
                  <span className="ml-auto flex items-center gap-1">
                    <i className="ri-time-line" /> {d.read}
                  </span>
                </div>
                <h3 className="mt-3 line-clamp-2 font-display text-base font-bold leading-snug text-ink-900 transition group-hover:text-ember-700">
                  {d.title}
                </h3>
                <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-ink-600">{d.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-ember-700">
                  阅读全文 <i className="ri-arrow-right-line transition group-hover:translate-x-1" />
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}